import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorNotice, Icon, SectionLabel } from '@hive/ui';
import { api, errorMessage, type Area, type Screen } from '../api/client';
import { useLiveScreens, useVenues } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { VenueHeader } from '../components/VenueHeader';

/**
 * Screens and areas, set up by the venue itself: venue staff and installers add screens,
 * name them as the venue calls them, put them in areas, pair players and remove them.
 */
export function SetupPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { venues } = useVenues();
  const venueId = params.get('venue') ?? venues?.find((v) => v.source === 'real')?.id ?? venues?.[0]?.id;
  const venue = venues?.find((v) => v.id === venueId);
  const { screens, update, replaceAll } = useLiveScreens(venue?.id);
  const [areas, setAreas] = useState<Area[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newScreen, setNewScreen] = useState({ name: '', areaId: '' });
  const [newArea, setNewArea] = useState('');
  const [editing, setEditing] = useState<{ kind: 'screen' | 'area'; id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<Screen | null>(null);
  const canControl = user?.permissions.includes('screens.control');

  const loadAreas = useCallback(async () => {
    if (!venue) return;
    const { data } = await api.GET('/v1/venues/{venueId}/areas', { params: { path: { venueId: venue.id } } });
    if (data) setAreas(data);
  }, [venue]);
  useEffect(() => void loadAreas(), [loadAreas]);

  const fail = (err: unknown) => setError(errorMessage(err));

  async function addScreen(e: FormEvent) {
    e.preventDefault();
    if (!venue || !newScreen.name.trim()) return;
    setError(null);
    const { data, error: err } = await api.POST('/v1/venues/{venueId}/screens/new', {
      params: { path: { venueId: venue.id } },
      body: { name: newScreen.name.trim(), ...(newScreen.areaId ? { areaId: newScreen.areaId } : {}) },
    });
    if (!data) return fail(err);
    replaceAll([...(screens ?? []), data]);
    setNewScreen({ name: '', areaId: newScreen.areaId });
    void loadAreas();
  }

  async function patchScreen(screen: Screen, body: { name?: string; areaId?: string }) {
    setError(null);
    const { data, error: err } = await api.PATCH('/v1/screens/{screenId}', { params: { path: { screenId: screen.id } }, body });
    if (!data) return fail(err);
    update(data);
    void loadAreas();
  }

  async function removeScreen(screen: Screen) {
    setError(null);
    const { error: err, response } = await api.DELETE('/v1/screens/{screenId}', { params: { path: { screenId: screen.id } } });
    setRemoving(null);
    if (response.status !== 204) return fail(err);
    replaceAll((screens ?? []).filter((s) => s.id !== screen.id));
    void loadAreas();
  }

  async function addArea(e: FormEvent) {
    e.preventDefault();
    if (!venue || !newArea.trim()) return;
    setError(null);
    const { data, error: err } = await api.POST('/v1/venues/{venueId}/areas', { params: { path: { venueId: venue.id } }, body: { name: newArea.trim() } });
    if (!data) return fail(err);
    setNewArea('');
    void loadAreas();
  }

  async function renameArea(area: Area, name: string) {
    setError(null);
    const { data, error: err } = await api.PATCH('/v1/areas/{areaId}', { params: { path: { areaId: area.id } }, body: { name } });
    if (!data) return fail(err);
    void loadAreas();
    if (venue) replaceAll((screens ?? []).map((s) => (s.zone?.id === area.id ? { ...s, zone: { id: area.id, name } } : s)));
  }

  async function removeArea(area: Area) {
    setError(null);
    const { error: err, response } = await api.DELETE('/v1/areas/{areaId}', { params: { path: { areaId: area.id } } });
    if (response.status !== 204) return fail(err);
    replaceAll((screens ?? []).map((s) => (s.zone?.id === area.id ? { ...s, zone: undefined } : s)));
    void loadAreas();
  }

  async function saveRename(e: FormEvent) {
    e.preventDefault();
    if (!editing || !editing.name.trim()) return;
    const name = editing.name.trim();
    if (editing.kind === 'screen') {
      const screen = screens?.find((s) => s.id === editing.id);
      if (screen && name !== screen.name) await patchScreen(screen, { name });
    } else {
      const area = areas.find((a) => a.id === editing.id);
      if (area && name !== area.name) await renameArea(area, name);
    }
    setEditing(null);
  }

  return (
    <main className="hv-root k-page">
      {venue && (
        <VenueHeader
          name={venue.name}
          context="Screens and areas"
          extra={
            canControl ? (
              <Link to={`/?venue=${venue.id}`} className="hv-btn hv-btn--secondary hv-btn--sm">
                Back to the screens
              </Link>
            ) : undefined
          }
        />
      )}
      {error && <ErrorNotice title={error} />}

      <div className="k-setup">
        <section className="k-card k-setup__card" aria-label="Screens">
          <SectionLabel>Screens</SectionLabel>
          <p className="k-hint">Name each screen the way the venue does, put it in an area, then pair a player to it.</p>
          <ul className="k-setup__list">
            {(screens ?? []).map((s) => (
              <li key={s.id} className="k-setup__row">
                <Icon name="screen" size={20} />
                {editing?.kind === 'screen' && editing.id === s.id ? (
                  <form className="k-setup__rename" onSubmit={saveRename}>
                    <input aria-label="Screen name" className="hv-input" value={editing.name} maxLength={80} autoFocus onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                    <button type="submit" className="hv-btn hv-btn--primary">
                      Save
                    </button>
                    <button type="button" className="hv-btn hv-btn--secondary" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <span className="k-setup__name">
                    <span className="k-strong">{s.name}</span>
                    <span className="k-hint">
                      {[s.code, s.status === 'unpaired' ? 'No player yet' : s.status === 'online' ? 'Player online' : 'Player offline'].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                )}
                <label className="k-setup__area">
                  <span className="hv-sr-only">Area for {s.name}</span>
                  <select className="hv-input" value={s.zone?.id ?? ''} onChange={(e) => patchScreen(s, { areaId: e.target.value })}>
                    <option value="">No area</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="k-setup__actions">
                  <button type="button" className="hv-btn hv-btn--secondary" onClick={() => setEditing({ kind: 'screen', id: s.id, name: s.name })}>
                    Rename
                  </button>
                  <button type="button" className="hv-btn hv-btn--outline-accent" onClick={() => navigate(`/pair?venue=${s.venueId}&screen=${s.id}`)}>
                    {s.status === 'unpaired' ? 'Pair a player' : 'Replace player'}
                  </button>
                  <button type="button" className="hv-btn hv-btn--secondary k-danger" onClick={() => setRemoving(s)}>
                    Remove
                  </button>
                </span>
              </li>
            ))}
            {screens && screens.length === 0 && <li className="k-hint">No screens yet. Add the first one below.</li>}
          </ul>
          <form className="k-setup__add" onSubmit={addScreen}>
            <input aria-label="New screen name" className="hv-input" placeholder="New screen, e.g. Snug bar" value={newScreen.name} maxLength={80} onChange={(e) => setNewScreen({ ...newScreen, name: e.target.value })} />
            <select aria-label="Area for the new screen" className="hv-input" value={newScreen.areaId} onChange={(e) => setNewScreen({ ...newScreen, areaId: e.target.value })}>
              <option value="">No area</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <button type="submit" className="hv-btn hv-btn--primary" disabled={!newScreen.name.trim()}>
              Add screen
            </button>
          </form>
        </section>

        <section className="k-card k-setup__card" aria-label="Areas">
          <SectionLabel>Areas</SectionLabel>
          <p className="k-hint">The parts of the venue: Main bar, Terrace, Dining. Screens are grouped by area.</p>
          <ul className="k-setup__list">
            {areas.map((a) => (
              <li key={a.id} className="k-setup__row">
                {editing?.kind === 'area' && editing.id === a.id ? (
                  <form className="k-setup__rename" onSubmit={saveRename}>
                    <input aria-label="Area name" className="hv-input" value={editing.name} maxLength={60} autoFocus onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                    <button type="submit" className="hv-btn hv-btn--primary">
                      Save
                    </button>
                    <button type="button" className="hv-btn hv-btn--secondary" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <span className="k-setup__name">
                    <span className="k-strong">{a.name}</span>
                    <span className="k-hint">
                      {a.screens} screen{a.screens === 1 ? '' : 's'}
                    </span>
                  </span>
                )}
                <span className="k-setup__actions">
                  <button type="button" className="hv-btn hv-btn--secondary" onClick={() => setEditing({ kind: 'area', id: a.id, name: a.name })}>
                    Rename
                  </button>
                  <button type="button" className="hv-btn hv-btn--secondary k-danger" onClick={() => removeArea(a)}>
                    Remove
                  </button>
                </span>
              </li>
            ))}
            {areas.length === 0 && <li className="k-hint">No areas yet.</li>}
          </ul>
          <form className="k-setup__add" onSubmit={addArea}>
            <input aria-label="New area name" className="hv-input" placeholder="New area, e.g. Beer garden" value={newArea} maxLength={60} onChange={(e) => setNewArea(e.target.value)} />
            <button type="submit" className="hv-btn hv-btn--primary" disabled={!newArea.trim()}>
              Add area
            </button>
          </form>
        </section>
      </div>

      {removing && (
        <div className="k-scrim" onClick={() => setRemoving(null)}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="rm-title" className="k-dialog" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="k-dialog__head">
              <h1 id="rm-title" className="k-dialog__title">
                Remove {removing.name}?
              </h1>
            </div>
            <div className="k-dialog__body">
              <p className="k-hint">{removing.device ? 'Its player is unpaired and shows a pairing code again.' : 'It has no player paired.'}</p>
            </div>
            <div className="k-dialog__foot">
              <button type="button" className="hv-btn hv-btn--secondary" onClick={() => setRemoving(null)}>
                Keep
              </button>
              <button type="button" className="hv-btn hv-btn--primary k-danger-solid" onClick={() => removeScreen(removing)}>
                Remove screen
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
